import { useEffect, useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";

const LanguageSelect = ({ selectedLanguage, setSelectedLanguage }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [minWidth, setMinWidth] = useState(0);
    const measureRef = useRef(null);

    const languages = useMemo(
        () => [
            { code: "en-GB", flag: "🇬🇧", name: "English" },
            { code: "de-DE", flag: "🇩🇪", name: "German" },
            { code: "fr-FR", flag: "🇫🇷", name: "French" },
        ],
        []
    );

    useEffect(() => {
        const lastSelectedLanguage = localStorage.getItem("lastSelectedLanguage");
        if (lastSelectedLanguage) {
            setSelectedLanguage(lastSelectedLanguage);
        } else {
            setSelectedLanguage("de-DE");
        }
    }, [setSelectedLanguage]);

    useEffect(() => {
        // Calculate the width needed for the longest option
        if (measureRef.current) {
            let maxWidth = 0;
            languages.forEach((lang) => {
                const tempSpan = document.createElement("span");
                tempSpan.style.visibility = "hidden";
                tempSpan.style.position = "absolute";
                tempSpan.style.whiteSpace = "nowrap";
                tempSpan.className = "text-large sm:text-large md:text-xl lg:text-2xl";
                tempSpan.textContent = `${lang.flag} ${t(lang.name)}`;
                document.body.appendChild(tempSpan);
                maxWidth = Math.max(maxWidth, tempSpan.offsetWidth);
                document.body.removeChild(tempSpan);
            });
            setMinWidth(maxWidth + 50); // Add padding for arrow and margins
        }
    }, [languages, t]);

    const handleLanguageChange = (languageCode) => {
        setSelectedLanguage(languageCode);
        localStorage.setItem("lastSelectedLanguage", languageCode);
        setIsOpen(false);
    };

    const selectedLang = languages.find((lang) => lang.code === selectedLanguage) || languages[1];

    return (
        <div className="relative">
            <button
                ref={measureRef}
                onClick={() => setIsOpen(!isOpen)}
                className="border rounded text-large sm:text-large md:text-xl lg:text-2xl p-2 text-center bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent flex items-center justify-between"
                style={{ minWidth: `${minWidth}px` }}
            >
                <span className="whitespace-nowrap">
                    {selectedLang.flag} {t(selectedLang.name)}
                </span>
                <svg
                    className={`w-5 h-5 transition-transform duration-200 ${
                        isOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                    />
                </svg>
            </button>

            {isOpen && (
                <div
                    className="absolute top-full left-0 mt-1 bg-white border rounded shadow-lg z-10"
                    style={{ minWidth: `${minWidth}px` }}
                >
                    {languages.map((language) => (
                        <button
                            key={language.code}
                            onClick={() => handleLanguageChange(language.code)}
                            className={`w-full text-left px-4 py-2 text-large sm:text-large md:text-xl lg:text-2xl hover:bg-gray-100 focus:outline-none focus:bg-gray-100 ${
                                selectedLanguage === language.code ? "bg-blue-50 text-blue-700" : ""
                            }`}
                        >
                            <span className="whitespace-nowrap">
                                {language.flag} {t(language.name)}
                            </span>
                        </button>
                    ))}
                </div>
            )}

            {isOpen && <div className="fixed inset-0 z-0" onClick={() => setIsOpen(false)} />}
        </div>
    );
};

export default LanguageSelect;
